'use client';

import { marketingStyles } from '@/components/marketing';
import React, { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { useOptionalBookingModal } from '@/components/modals/BookingModalContext';
import { bookingOfferEvents } from '@/components/PostHogProvider';
import type { BookingModalRequest } from '@marketing-optimizer/ontology';

const LEGACY_CLASSES =
  'inline-flex items-center justify-center rounded-full bg-[#B45309] px-6 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#92400E] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#B45309] motion-reduce:transition-none';

/**
 * Measures a visible offer separately from clicks and successfully saved requests.
 *
 * `link` renders the shared underlined text-button skin, for an offer that sits
 * beneath a primary action as the smaller step (the report's question link,
 * HAL-2081); `className` carries the surface's own ink and size.
 *
 * The modal provider is optional so a component spec can render the offer
 * without mounting it; without a provider the click is still measured and opens
 * nothing.
 */
export function BookingOfferButton({
  request,
  children,
  placement,
  visualVariant = 'legacy',
  className,
}: {
  visualVariant?: 'legacy' | 'marketing' | 'link';
  request: BookingModalRequest;
  children: React.ReactNode;
  placement: 'hero' | 'footer' | 'guide' | 'failure' | 'report-close' | 'report-sidebar';
  className?: string;
}): React.ReactElement {
  const bookingModal = useOptionalBookingModal();
  const ref = useRef<HTMLButtonElement>(null);
  const offer = request.kind === 'consultation' ? request.offer : request.kind;
  const failureCode = request.kind === 'manual-review' ? request.failureCode : undefined;

  useEffect(() => {
    const button = ref.current;
    if (!button || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          bookingOfferEvents.viewed({
            offer,
            placement,
            pagePath: window.location.pathname,
            ...(failureCode && { failureCode }),
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(button);
    return () => observer.disconnect();
  }, [offer, placement, failureCode]);

  return (
    <button
      ref={ref}
      type="button"
      aria-haspopup="dialog"
      onClick={() => {
        bookingOfferEvents.clicked({
          offer,
          placement,
          pagePath: window.location.pathname,
          ...(failureCode && { failureCode }),
        });
        bookingModal?.openRequest(request);
      }}
      className={clsx(
        visualVariant === 'marketing'
          ? marketingStyles.btn
          : visualVariant === 'link'
            ? marketingStyles.textButton
            : LEGACY_CLASSES,
        className
      )}
    >
      {children}
    </button>
  );
}
